import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { platformAPI } from '../../lib/api';
import api from '../../lib/api';
import { TrendingUp, RefreshCw, Clock, Ban } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Sub-components
import { PlatformStatsCards } from './PlatformStatsCards';
import { InstitutionsTable } from './InstitutionsTable';
import { ActivityLog } from './ActivityLog';
import { InstitutionDetailModal } from './InstitutionDetailModal';
import { apiErrorMessage } from '../../lib/utils';

export default function PlatformDashboard() {
  const { user, isPlatformAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [suspendModal, setSuspendModal] = useState({ open: false, institution: null });
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [institutionDetailModal, setInstitutionDetailModal] = useState({ open: false, institution: null });
  const [institutionUsers, setInstitutionUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, institutionsRes, activityRes] = await Promise.all([
        api.get('/platform/stats'),
        platformAPI.getInstitutions(),
        api.get('/platform/activity-log').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setInstitutions(institutionsRes.data?.institutions || institutionsRes.data || []);
      setActivityLog(activityRes.data || []);
    } catch (error) {
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = useCallback(async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      const res = await platformAPI.getInstitutions(params);
      setInstitutions(res.data?.institutions || res.data || []);
    } catch (error) {
      toast.error('Failed to search institutions');
    }
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchInstitutions(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, fetchInstitutions]);

  useEffect(() => { fetchData(); }, []);

  const handleSuspend = async () => {
    setActionLoading(true);
    try {
      await platformAPI.suspendInstitution(suspendModal.institution.id, { reason: suspendReason });
      toast.success('Institution suspended successfully');
      setSuspendModal({ open: false, institution: null });
      setSuspendReason('');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to suspend institution'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (institution) => {
    try {
      await platformAPI.reactivateInstitution(institution.id);
      toast.success('Institution reactivated successfully');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to reactivate institution'));
    }
  };

  const handleExtendTrial = async (institution) => {
    try {
      await api.post(`/platform/extend-trial/${institution.id}`, { additional_days: 15 });
      toast.success('Trial extended by 15 days');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to extend trial'));
    }
  };

  const handleIncreasePatientLimit = async (institution) => {
    try {
      await api.post(`/platform/increase-patient-limit/${institution.id}`, { increase_percentage: 10 });
      toast.success('Patient limit increased by 10%');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to increase limit'));
    }
  };

  const getStatusBadge = (status, daysRemaining) => {
    const styles = {
      trial: 'bg-amber-100 text-amber-700',
      active: 'bg-emerald-100 text-emerald-700',
      expired: 'bg-red-100 text-red-700',
      suspended: 'bg-slate-100 text-slate-700',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
        <Clock className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
        {status === 'trial' && daysRemaining !== undefined && ` (${daysRemaining}d)`}
      </span>
    );
  };

  const fetchInstitutionUsers = async (institutionId) => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/users');
      const usersArray = response.data.users || response.data || [];
      setInstitutionUsers(usersArray.filter(u => u.institution_id === institutionId));
    } catch (error) {
      toast.error('Failed to load institution users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleViewInstitution = (institution) => {
    setInstitutionDetailModal({ open: true, institution });
    fetchInstitutionUsers(institution.id);
  };

  if (!isPlatformAdmin) {
    return (
      <div className="p-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500">Access restricted to platform administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded"></div>)}
          </div>
          <div className="h-96 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="platform-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage all institutions and monitor platform health</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => window.location.href = '/platform/analytics'} className="gap-2 bg-red-500 hover:bg-red-600" data-testid="view-analytics-btn">
            <TrendingUp className="w-4 h-4" />
            View Analytics
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      <PlatformStatsCards stats={stats} />

      <InstitutionsTable
        institutions={institutions}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        getStatusBadge={getStatusBadge}
        onViewInstitution={handleViewInstitution}
        onExtendTrial={handleExtendTrial}
        onIncreasePatientLimit={handleIncreasePatientLimit}
        onReactivate={handleReactivate}
        onSuspend={(inst) => setSuspendModal({ open: true, institution: inst })}
      />

      <ActivityLog activityLog={activityLog} />

      {/* Suspend Modal */}
      <Dialog open={suspendModal.open} onOpenChange={(open) => !open && setSuspendModal({ open: false, institution: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Institution</DialogTitle>
            <DialogDescription>
              Are you sure you want to suspend &quot;{suspendModal.institution?.institution_name}&quot;?
              Users will not be able to log in while suspended.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for suspension</Label>
              <Input id="reason" placeholder="Enter reason..." value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} data-testid="suspend-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendModal({ open: false, institution: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleSuspend} disabled={actionLoading} data-testid="confirm-suspend">
              {actionLoading ? 'Suspending...' : 'Suspend Institution'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstitutionDetailModal
        open={institutionDetailModal.open}
        onOpenChange={(open) => setInstitutionDetailModal({ open, institution: open ? institutionDetailModal.institution : null })}
        institution={institutionDetailModal.institution}
        users={institutionUsers}
        loadingUsers={loadingUsers}
        onUsersRefresh={() => fetchInstitutionUsers(institutionDetailModal.institution?.id)}
      />
    </div>
  );
}
